/**
 * @fileoverview Custom components and utilities for CMMS Frontend
 * This file re-exports from modular utilities for backward compatibility.
 *
 * New code should import directly from:
 * - '../services/api' for API functions (GET, POST, PUT, DELETE)
 * - '../utils/date' for date formatting
 * - '../utils/formatting' for number formatting
 * - '../utils/helpers' for helper functions
 * - '../utils/localStorage' for storage functions
 * - '../context/AuthContext' for auth state
 */

import React, { Component } from "react";
import Swal from "sweetalert2";
import { Modal as BootstrapModal } from "react-bootstrap";
// src/utils/changeLanguage.js
import th from "./language/th.json";
import en from "./language/en.json";
import { translateMessage, translateTitle } from "../utils/backendMessage";

import "../assets/css/bootstrap.css";
import "../assets/css/style.css";

// ============================================================================
// Import from new modular utilities
// ============================================================================
import { GET, GET_FILE, POST, PUT, DELETE, UPLOAD, MAX_UPLOAD_SIZE, loading } from "../services/api";
import { format_date } from "../utils/date";
import { total, float, toFixed } from "../utils/formatting";
import { random_charactor, copyText, params, isOptionActive, activeOptions, activeTreeOptions, sortOptions, compareLabels } from "../utils/helpers";

// ============================================================================
// Legacy exports for backward compatibility
// ============================================================================

/** @deprecated Use getTokens() from '../utils/localStorage' */
const tokens = localStorage.getItem("cm-acc-tk") || "";

// อ่านค่าที่เก็บไว้ในเบราว์เซอร์แบบไม่โยน error - สองตัวข้างล่างอ่านตอน import module
// ถ้าค่าเสีย (เช่น ถูกแก้มือ/เขียนค้าง) แล้วปล่อยให้ JSON.parse โยน จะพังทั้งแอปตั้งแต่โหลด ไม่ใช่แค่หน้าเดียว
const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** @deprecated Use getProfile() from '../utils/localStorage' */
const profile = readJson("cm-profile");

/** @deprecated Use getProfile() from '../utils/localStorage' */
const company = readJson("cm-company");

/** Required field indicator */
const required = <span className="text-danger">*</span>;

/**
 * Alert wrapper using SweetAlert2
 * @deprecated Use showAlert from '../services/api'
 */
const alert = (url, icon, title, detail, confirm, cancel) => {
  const modals = document.getElementsByClassName("swal2-modal");
  const icons = document.getElementsByClassName("swal2-icon");
  const hasBangIcon = Array.from(icons).some(
    (el) => el.textContent.trim() === "!",
  );
  if (modals.length >= 2 || hasBangIcon) return;

  return Swal.fire({
    icon: icon || "warning",
    title: translateTitle(title || (icon === "success" ? "Success" : "Warning")),
    text: translateMessage(
      detail ||
        (icon === "success"
          ? "The transaction was completed successfully."
          : " "),
    ),
    confirmButtonText: translateMessage(confirm) || (cancel ? t("confirm") : t("ok")),
    showCancelButton: !!cancel,
    cancelButtonText: translateMessage(cancel) || t("cancel"),
    background: "#fff",
    color: "#000000",
    confirmButtonColor: "#212529",
    cancelButtonColor: "unset",
  }).then(({ isConfirmed }) => {
    if (url) {
      if (url === "#") window.location.reload();
      else window.location.href = url;
    }
    return isConfirmed;
  });
};

/**
 * Status badge component
 * @param {string} status - Status text
 * @param {string} color - Bootstrap color name
 * @param {string} icon - Icon character
 */
const Status = (status, color, icon) => {
  return (
    <div className="d-flex">
      <span
        className={`bg-${color || "primary"}-light text-${
          color || "primary"
        } border-status-${
          color || "primary"
        } px-2 pt-0 rounded-4 text-nowrap d-flex text-12 mx-1`}
        style={{ padding: "2px 0 0 2px" }}
      >
        {status}
        {icon && (
          <span className={`icon ms-1 text-${color || "primary"}`}>{icon}</span>
        )}
      </span>
    </div>
  );
};

// ============================================================================
// DatePicker Component
// ============================================================================
class DatePicker extends Component {
  calendarRef = React.createRef();
  inputRef = React.createRef();
  state = {
    value: "",
    calPos: null,
    default: new Date(),
    array_date: [],
    array_month: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    array_year: [],
    showCalendar: false,
    min: new Date(new Date().setFullYear(new Date().getFullYear() - 100)),
    max: new Date(new Date().setFullYear(new Date().getFullYear() + 100)),
  };

  componentDidMount = () => {
    let year = [];
    for (let i = -100; i < 100; i++) {
      year.push(new Date().getFullYear() + i);
    }
    this.setState({ array_year: year });
    if (this.props.value) {
      this.setState({
        default: new Date(this.props.value),
        value: new Date(this.props.value),
      });
    }
    if (this.props.min) {
      this.setState({ min: new Date(this.props.min) });
    }
    if (this.props.max) {
      this.setState({ max: new Date(this.props.max) });
    }
    this.getDates();
  };

  componentWillReceiveProps = (props) => {
    if (props.min) {
      this.setState({ min: new Date(props.min) });
    } else {
      this.setState({
        min: new Date(new Date().setFullYear(new Date().getFullYear() - 100)),
      });
    }
    if (props.max) {
      this.setState({ max: new Date(props.max) });
    } else {
      this.setState({
        max: new Date(new Date().setFullYear(new Date().getFullYear() + 100)),
      });
    }
    if (props.value) {
      this.setState({
        default: new Date(props.value),
        value: new Date(props.value),
      });
    } else {
      this.setState({ default: new Date(), value: "" });
    }
  };

  onChangeDate = (option) => {
    if (option === "") {
      this.props.onChange("");
    } else {
      this.props.onChange(
        Number(option.getFullYear()) +
          "-" +
          ("0" + (Number(option.getMonth()) + 1)).slice(-2) +
          "-" +
          ("0" + option.getDate()).slice(-2),
      );
    }
    this.setState({ value: option, showCalendar: false });
  };

  getDates = () => {
    let dates = [];
    let date = new Date(
      this.state.default.getFullYear(),
      this.state.default.getMonth(),
      1,
    );
    for (let d = 0; d < new Date(date).getDay(); d++) {
      dates.push("");
    }
    while (date.getMonth() === this.state.default.getMonth()) {
      dates.push(new Date(date).getDate());
      date.setDate(date.getDate() + 1);
    }
    this.setState({ array_date: dates });
  };

  render() {
    const isTh = this.props.type === "th";
    const thMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
    const thDays = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
    return (
      <div style={{ position: "relative", width: "100%", fontSize: "0.8rem" }}>
        {this.state.showCalendar && (
          <div
            style={{
              bottom: 0,
              left: 0,
              top: 0,
              right: 0,
              backgroundColor: "#0000",
              zIndex: 99998,
              position: "fixed",
            }}
            onClick={() => this.setState({ showCalendar: false })}
          />
        )}
        <div style={{ width: "100%" }}>
          <div style={{ position: "relative" }}>
            <input
              ref={this.inputRef}
              className={this.props.className}
              style={{ cursor: "pointer" }}
              type="text"
              value={
                this.state.value && typeof this.state.value === "object"
                  ? ("0" + this.state.value.getDate()).slice(-2) +
                    "/" +
                    ("0" + (Number(this.state.value.getMonth()) + 1)).slice(
                      -2,
                    ) +
                    "/" +
                    (isTh
                      ? this.state.value.getFullYear() + 543
                      : this.state.value.getFullYear())
                  : this.state.value
              }
              placeholder={this.props.placeholder || (isTh ? "วว/ดด/ปปปป" : "dd/mm/yyyy")}
              onFocus={() => {
                const rect = this.inputRef.current ? this.inputRef.current.getBoundingClientRect() : null;
                const calWidth = 300;
                const calHeight = 340;
                let left = rect ? rect.left : 0;
                let top = rect ? rect.bottom + 4 : 0;
                if (rect) {
                  if (left + calWidth > window.innerWidth) left = window.innerWidth - calWidth - 8;
                  if (left < 8) left = 8;
                  if (rect.bottom + calHeight > window.innerHeight) top = rect.top - calHeight - 4;
                  if (top < 8) top = 8;
                }
                this.setState({
                  showCalendar: true,
                  calPos: { left, top },
                  default: this.state.value
                    ? new Date(this.state.value)
                    : new Date(),
                });
                setTimeout(() => this.getDates(), 1);
              }}
              onChange={(e) => this.setState({ value: e.target.value })}
              onKeyUp={(e) => {
                if (e.key === "Enter") e.target.blur();
              }}
              onBlur={() => {
                const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
                if (regex.test(this.state.value)) {
                  const parts = this.state.value.split("/");
                  const year =
                    Number(parts[2]) > 2100
                      ? Number(parts[2]) - 543
                      : Number(parts[2]);
                  this.setState({
                    default: new Date(
                      year,
                      Number(parts[1]) - 1,
                      Number(parts[0]),
                    ),
                  });
                  setTimeout(() => {
                    this.onChangeDate(
                      new Date(
                        this.state.default.getFullYear(),
                        this.state.default.getMonth(),
                        this.state.default.getDate(),
                      ),
                    );
                  }, 10);
                } else if (
                  this.state.value.length === 8 &&
                  Number(this.state.value)
                ) {
                  const year =
                    Number(this.state.value.slice(-4)) > 2100
                      ? Number(this.state.value.slice(-4)) - 543
                      : Number(this.state.value.slice(-4));
                  this.setState({
                    default: new Date(
                      year,
                      Number(this.state.value.slice(2, 4)) - 1,
                      Number(this.state.value.slice(0, 2)),
                    ),
                  });
                  setTimeout(() => {
                    this.onChangeDate(
                      new Date(
                        this.state.default.getFullYear(),
                        this.state.default.getMonth(),
                        this.state.default.getDate(),
                      ),
                    );
                  }, 10);
                } else if (typeof this.state.value !== "object") {
                  this.setState({ value: "" });
                }
              }}
              disabled={this.props.disabled}
              maxLength={10}
            />
            <svg
              style={{ position: "absolute", right: 12, top: 8 }}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="#d2d2d2"
              viewBox="0 0 448 512"
            >
              <path d="M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l80 0 0 56-80 0 0-56zm0 104l80 0 0 64-80 0 0-64zm128 0l96 0 0 64-96 0 0-64zm144 0l80 0 0 64-80 0 0-64zm80-48l-80 0 0-56 80 0 0 56zm0 160l0 40c0 8.8-7.2 16-16 16l-64 0 0-56 80 0zm-128 0l0 56-96 0 0-56 96 0zm-144 0l0 56-64 0c-8.8 0-16-7.2-16-16l0-40 80 0zM272 248l-96 0 0-56 96 0 0 56z" />
            </svg>
          </div>
          {this.state.showCalendar && (
            <div
              ref={this.calendarRef}
              style={{
                zIndex: 99999,
                width: 300,
                position: "fixed",
                left: this.state.calPos ? this.state.calPos.left : 0,
                top: this.state.calPos ? this.state.calPos.top : 0,
                paddingTop: "0.3rem",
                paddingBottom: "0.3rem",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                wordWrap: "break-word",
                backgroundColor: "#ffffff",
                backgroundClip: "border-box",
                border: "1px solid rgba(0,0,0,.125)",
                borderRadius: 8,
                boxShadow: "0 0.3rem 1rem rgba(0, 0, 0, .15)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ cursor: "pointer", padding: "1rem" }}
                  onClick={() => {
                    let date = this.state.default;
                    date.setMonth(date.getMonth() - 1);
                    this.setState({ default: date });
                    setTimeout(() => this.getDates(), 1);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    width="12"
                    height="12"
                  >
                    <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
                  </svg>
                </span>
                <select
                  style={{ width: 100, borderWidth: 0, textAlign: "center" }}
                  onChange={(e) => {
                    let date = this.state.default;
                    date.setMonth(e.target.value);
                    this.setState({ default: date });
                    setTimeout(() => this.getDates(), 1);
                  }}
                  value={
                    this.state.default ? this.state.default.getMonth() : " "
                  }
                >
                  {this.state.array_month.map((item, index) => (
                    <option key={`month-${index}`} value={index}>
                      {isTh ? thMonths[index] : item}
                    </option>
                  ))}
                </select>
                <label
                  style={{
                    marginLeft: "0.3rem",
                    marginRight: "0.3rem",
                    paddingBottom: 0,
                    cursor: "pointer",
                  }}
                >
                  {this.props.type === "th" ? "พ.ศ." : "ค.ศ."}
                </label>
                <select
                  style={{ width: 60, borderWidth: 0, textAlign: "center" }}
                  onChange={(e) => {
                    let date = this.state.default;
                    date.setFullYear(e.target.value);
                    this.setState({ default: date });
                    setTimeout(() => this.getDates(), 1);
                  }}
                  value={
                    this.state.default ? this.state.default.getFullYear() : " "
                  }
                >
                  {this.state.array_year.map((item, index) => (
                    <option key={`year-${index}`} value={item}>
                      {this.props.type === "th" ? item + 543 : item}
                    </option>
                  ))}
                </select>
                <span
                  style={{ cursor: "pointer", padding: "1rem" }}
                  onClick={() => {
                    let date = this.state.default;
                    date.setMonth(date.getMonth() + 1);
                    this.setState({ default: date });
                    setTimeout(() => this.getDates(), 1);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    width="12"
                    height="12"
                  >
                    <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
                  </svg>
                </span>
              </div>
              <div
                style={{
                  marginBottom: "1rem",
                  marginLeft: 0,
                  marginRight: 0,
                  width: "100%",
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                {(isTh ? thDays : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]).map(
                  (item, index) => (
                    <div
                      key={"day_" + index}
                      style={{
                        width: "14.28%",
                        padding: "0.25rem 0",
                        textAlign: "center",
                      }}
                    >
                      {item}
                    </div>
                  ),
                )}
              </div>
              <div
                style={{
                  marginTop: "-1rem",
                  width: "100%",
                  display: "flex",
                  flexWrap: "wrap",
                  height: 220,
                }}
              >
                {this.state.array_date.map((item, index) => {
                  const isSelected =
                    this.state.value &&
                    typeof this.state.value === "object" &&
                    item &&
                    this.state.value.getDate() === item &&
                    this.state.value.getMonth() ===
                      this.state.default.getMonth() &&
                    this.state.value.getFullYear() ===
                      this.state.default.getFullYear();
                  const dateObj = new Date(
                    this.state.default.getFullYear(),
                    this.state.default.getMonth(),
                    item,
                  );
                  const isInRange =
                    dateObj > this.state.min.getTime() &&
                    dateObj < this.state.max.getTime();

                  return (
                    <div
                      key={"date_" + index}
                      style={{
                        width: "14.28%",
                        paddingTop: "0.3rem",
                        paddingBottom: "0.3rem",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isInRange && item ? "pointer" : "default",
                        backgroundColor: isSelected ? "#0d6efd" : "transparent",
                        color: isSelected
                          ? "#ffffff"
                          : isInRange
                            ? "#000"
                            : "#e1e2e2",
                      }}
                      onClick={() => {
                        if (isInRange && item) {
                          this.onChangeDate(
                            new Date(
                              this.state.default.getFullYear(),
                              this.state.default.getMonth(),
                              item,
                            ),
                          );
                        }
                      }}
                    >
                      {item}
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingLeft: "0.3rem",
                  paddingRight: "0.3rem",
                }}
              >
                {this.props.clearable !== false ? (
                  <label
                    style={{ cursor: "pointer", color: "#0d6efd" }}
                    onClick={() => this.onChangeDate("")}
                  >
                    clear
                  </label>
                ) : (
                  <label></label>
                )}
                <label
                  style={{ cursor: "pointer", color: "#0d6efd" }}
                  onClick={() => this.onChangeDate(new Date())}
                >
                  Today
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

// ============================================================================
// DateRangePicker Component
// ============================================================================
class DateRangePicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      start_date: props.value?.start_date
        ? new Date(props.value.start_date)
        : null,
      end_date: props.value?.end_date ? new Date(props.value.end_date) : null,
      hoverDate: null,
      default: new Date(),
      array_date: [],
      array_month: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      array_year: [],
      showCalendar: false,
    };
    this.calendarRef = React.createRef();
    this.inputWrapperRef = React.createRef();
  }

  componentDidMount() {
    let year = [];
    for (let i = -100; i < 100; i++) year.push(new Date().getFullYear() + i);
    this.setState({ array_year: year });
    this.getDates();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        start_date: this.props.value?.start_date
          ? new Date(this.props.value.start_date)
          : null,
        end_date: this.props.value?.end_date
          ? new Date(this.props.value.end_date)
          : null,
      });
    }
    if (this.state.showCalendar && !prevState.showCalendar) {
      this.adjustCalendarPosition();
    }
  }

  adjustCalendarPosition = () => {
    const calendar = this.calendarRef.current;
    const inputWrapper = this.inputWrapperRef.current;
    if (!calendar || !inputWrapper) return;
    const rect = calendar.getBoundingClientRect();
    const inputRect = inputWrapper.getBoundingClientRect();
    let style = {};
    if (rect.right > window.innerWidth) {
      style.right = 0;
      style.left = "auto";
    }
    if (rect.bottom > window.innerHeight) {
      style.bottom = inputRect.height + 8;
      style.top = "auto";
      style.marginTop = 0;
    }
    Object.assign(calendar.style, style);
  };

  getDates = () => {
    let dates = [];
    let date = new Date(
      this.state.default.getFullYear(),
      this.state.default.getMonth(),
      1,
    );
    for (let d = 0; d < date.getDay(); d++) dates.push("");
    while (date.getMonth() === this.state.default.getMonth()) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    this.setState({ array_date: dates });
  };

  handleDateClick = (date) => {
    const { start_date, end_date } = this.state;
    if (!start_date || (start_date && end_date)) {
      this.setState({ start_date: date, end_date: null }, () => {
        this.props.onChange?.({ start_date: date, end_date: null });
      });
    } else if (start_date && !end_date) {
      if (date >= start_date) {
        this.setState({ end_date: date }, () => {
          this.props.onChange?.({
            start_date: this.state.start_date,
            end_date: date,
          });
        });
      } else {
        this.setState({ start_date: date, end_date: start_date }, () => {
          this.props.onChange?.({ start_date: date, end_date: start_date });
        });
      }
    }
  };

  isInRange = (date) => {
    const { start_date, end_date, hoverDate } = this.state;
    if (!start_date) return false;
    if (start_date && !end_date && hoverDate)
      return date >= start_date && date <= hoverDate;
    if (start_date && end_date) return date >= start_date && date <= end_date;
    return false;
  };

  render() {
    const {
      array_date,
      array_month,
      default: current,
      start_date,
      end_date,
      showCalendar,
    } = this.state;
    const isTh = this.props.type === "th";
    const thMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
    const thDays = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
    return (
      <div
        style={{ position: "relative", width: "100%", fontSize: "0.8rem" }}
        ref={this.inputWrapperRef}
      >
        {showCalendar && (
          <div
            style={{
              bottom: 0,
              left: 0,
              top: 0,
              right: 0,
              backgroundColor: "#0000",
              zIndex: 99998,
              position: "fixed",
            }}
            onClick={() => this.setState({ showCalendar: false })}
          />
        )}
        <div style={{ position: "relative" }}>
          <input
            className={this.props.className}
            readOnly
            type="text"
            onFocus={() => this.setState({ showCalendar: true })}
            value={
              start_date
                ? `${format_date(start_date, "dd/mm/yyyy", isTh ? "th" : "en")} - ${end_date ? format_date(end_date, "dd/mm/yyyy", isTh ? "th" : "en") : ""}`
                : ""
            }
            placeholder={isTh ? "เลือกช่วงวันที่" : "Select date range"}
            style={{ width: "100%", padding: "0.35rem 0.5rem" }}
          />
          <svg
            style={{ position: "absolute", right: 12, top: 8 }}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="#d2d2d2"
            viewBox="0 0 448 512"
          >
            <path d="M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l80 0 0 56-80 0 0-56zm0 104l80 0 0 64-80 0 0-64zm128 0l96 0 0 64-96 0 0-64zm144 0l80 0 0 64-80 0 0-64zm80-48l-80 0 0-56 80 0 0 56zm0 160l0 40c0 8.8-7.2 16-16 16l-64 0 0-56 80 0zm-128 0l0 56-96 0 0-56 96 0zm-144 0l0 56-64 0c-8.8 0-16-7.2-16-16l0-40 80 0zM272 248l-96 0 0-56 96 0 0 56z" />
          </svg>
        </div>
        {showCalendar && (
          <div
            id="calendar-range"
            ref={this.calendarRef}
            style={{
              zIndex: 99998,
              width: 320,
              position: "absolute",
              marginTop: "0.2rem",
              padding: "0.5rem",
              backgroundColor: "#fff",
              border: "1px solid rgba(0,0,0,.125)",
              borderRadius: 8,
              boxShadow: "0 0.3rem 1rem rgba(0,0,0,.15)",
              top: "100%",
              left: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "0.5rem",
              }}
            >
              <span
                style={{ cursor: "pointer", padding: "1rem" }}
                onClick={() => {
                  let d = new Date(current);
                  d.setMonth(d.getMonth() - 1);
                  this.setState({ default: d }, this.getDates);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  width="12"
                  height="12"
                  fill="#000"
                >
                  <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
                </svg>
              </span>
              <div>
                {isTh ? thMonths[current.getMonth()] : array_month[current.getMonth()]} {isTh ? current.getFullYear() + 543 : current.getFullYear()}
              </div>
              <span
                style={{ cursor: "pointer", padding: "1rem" }}
                onClick={() => {
                  let d = new Date(current);
                  d.setMonth(d.getMonth() + 1);
                  this.setState({ default: d }, this.getDates);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  width="12"
                  height="12"
                  fill="#000"
                >
                  <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
                </svg>
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {(isTh ? thDays : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]).map((d, i) => (
                <div
                  key={i}
                  style={{
                    width: "14.28%",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {array_date.map((date, idx) => {
                if (!date)
                  return (
                    <div key={idx} style={{ width: "14.28%", height: 30 }} />
                  );
                const isSelected =
                  (start_date &&
                    date.toDateString() === start_date.toDateString()) ||
                  (end_date && date.toDateString() === end_date.toDateString());
                const inRange = this.isInRange(date);
                return (
                  <div
                    key={idx}
                    style={{
                      width: "14.28%",
                      height: 30,
                      textAlign: "center",
                      lineHeight: "30px",
                      margin: "2px 0",
                      backgroundColor: isSelected
                        ? "#0d6efd"
                        : inRange
                          ? "rgba(13,110,253,0.3)"
                          : "transparent",
                      color: isSelected ? "#fff" : "#000",
                      cursor: "pointer",
                      borderTopLeftRadius:
                        start_date &&
                        date.toDateString() === start_date.toDateString()
                          ? 12
                          : 0,
                      borderBottomLeftRadius:
                        start_date &&
                        date.toDateString() === start_date.toDateString()
                          ? 12
                          : 0,
                      borderTopRightRadius:
                        end_date &&
                        date.toDateString() === end_date.toDateString()
                          ? 12
                          : 0,
                      borderBottomRightRadius:
                        end_date &&
                        date.toDateString() === end_date.toDateString()
                          ? 12
                          : 0,
                    }}
                    onClick={() => this.handleDateClick(date)}
                    onMouseEnter={() => this.setState({ hoverDate: date })}
                    onMouseLeave={() => this.setState({ hoverDate: null })}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingLeft: "0.3rem",
                paddingRight: "0.3rem",
                marginTop: "0.5rem",
              }}
            >
              {this.props.clearable !== false ? (
                <label
                  style={{ cursor: "pointer", color: "#0d6efd" }}
                  onClick={() => {
                    this.setState({ start_date: null, end_date: null });
                    this.props.onChange?.({ start_date: null, end_date: null });
                  }}
                >
                  {isTh ? "ล้าง" : "clear"}
                </label>
              ) : (
                <label />
              )}
              <label
                style={{ cursor: "pointer", color: "#0d6efd" }}
                onClick={() => {
                  this.setState({ start_date: new Date(), end_date: null });
                  this.props.onChange?.({
                    start_date: new Date(),
                    end_date: null,
                  });
                }}
              >
                {isTh ? "วันนี้" : "Today"}
              </label>
            </div>
          </div>
        )}
      </div>
    );
  }
}

// ============================================================================
// Select Component
// ============================================================================
class Select extends Component {
  inputRef = React.createRef();
  searchTimer = null;
  state = { showSelectOption: false, search: "", selected: "", pos: null, picked: null };

  // โหมด remote (opt-in): ส่ง prop onSearch(keyword) มา = ให้ parent ไปดึงข้อมูลจาก API เอง
  // ใช้กับ dropdown ที่ข้อมูลเยอะเกินจะดึงมาทั้งตาราง (อะไหล่ / ลูกค้า / asset)
  // ไม่ส่ง onSearch มา = ทำงานเหมือนเดิมทุกประการ (กรองจาก options ที่ส่งเข้ามา)
  // props เสริมของโหมดนี้: loading (bool), searchDelay (ms, default 350)
  isRemote = () => typeof this.props.onSearch === "function";

  // โหมดรายการกว้างตามเนื้อหา (opt-in): ส่ง prop dropdownAutoWidth มา = ช่องเลือกคงขนาดเดิม
  // แต่ตอนกางรายการจะขยายให้พอกับป้ายที่ยาวที่สุดและไม่ตัดขึ้นบรรทัดใหม่ (ชื่อช่าง/ลูกค้าเห็นเต็มบรรทัดเดียว)
  // ไม่ส่งมา = รายการกว้างเท่าช่องเลือกเหมือนเดิมทุกประการ
  isAutoWidth = () => !!this.props.dropdownAutoWidth;

  // เรียงรายการ ก-ฮ -> a-z ให้อัตโนมัติทุกที่ที่ใช้ Select
  // ส่ง sort={false} เมื่อลำดับเดิมมีความหมาย (ระดับความสำคัญ, ช่วงเวลา, ลำดับสถานะ)
  // จำผลไว้ตาม reference ของ options เพื่อไม่ต้องเรียงใหม่ทุกครั้งที่พิมพ์ค้นหา
  getOptions = () => {
    const { options, sort } = this.props;
    if (sort === false || !Array.isArray(options)) return options;
    if (this.sortedSource !== options) {
      this.sortedSource = options;
      this.sortedOptions = sortOptions(options);
    }
    return this.sortedOptions;
  };

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  // หน่วงก่อนยิง เพื่อไม่ให้พิมพ์ 1 ตัวอักษร = 1 request
  emitSearch = (keyword, immediate = false) => {
    if (!this.isRemote()) return;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (immediate) {
      this.props.onSearch(keyword);
      return;
    }
    this.searchTimer = setTimeout(
      () => this.props.onSearch(keyword),
      this.props.searchDelay ?? 350
    );
  };

  // โหมด remote: backend กรองมาให้แล้ว (และค้นได้กว้างกว่า label เช่น รหัส/บาร์โค้ด)
  // ถ้ากรองซ้ำฝั่งนี้ ผลที่ตรงแต่ไม่มีคำค้นอยู่ใน label จะหายไปทั้งหมด
  matchesSearch = (e) => {
    if (this.isRemote()) return true;
    if (!this.state.search) return true;
    const search = this.state.search.toLowerCase();
    return (
      e.label.toLowerCase().includes(search) ||
      (e.group && e.group.toLowerCase().includes(search))
    );
  };

  // โหมด remote: ตัวที่เลือกไว้อาจไม่อยู่ใน options ชุดปัจจุบัน (ผู้ใช้ค้นคำอื่นต่อ)
  // จึงจำ label ของตัวที่เพิ่งเลือกไว้ใช้แสดงแทน
  getSelectedLabel = () => {
    const option = this.props.options?.find((e) => e.value === this.props.value);
    if (option) return option.label;
    if (this.state.picked && this.state.picked.value === this.props.value)
      return this.state.picked.label;
    return "";
  };

  render() {
    const options = this.getOptions();
    return (
      <div
        style={{ position: "relative", padding: 0 }}
        className={this.props.className}
      >
        {this.state.showSelectOption && (
          <div
            style={{
              bottom: 0,
              left: 0,
              top: 0,
              right: 0,
              backgroundColor: "#0000",
              zIndex: 99998,
              position: "fixed",
            }}
            onClick={() => this.setState({ showSelectOption: false })}
          />
        )}
        <div style={{ position: "relative", width: "100%" }}>
          {!this.state.search &&
            !this.state.showSelectOption &&
            options?.find((e) => e.value === this.props.value)
              ?.front && (
              <div className="position-absolute mx-2 mt-1">
                {
                  options?.find((e) => e.value === this.props.value)
                    ?.front
                }
              </div>
            )}
          <input
            ref={this.inputRef}
            className={this.props.className
              ?.replaceAll("mb-2", "")
              .replaceAll("mb-3", "")
              .replaceAll("mb-4", "")
              .replaceAll("mb-5", "")}
            style={{
              width: "100%",
              backgroundColor: this.props.disabled ? "#e9ecef" : "#00000000",
              border: 0,
              paddingLeft:
                !this.state.search &&
                !this.state.showSelectOption &&
                options?.find((e) => e.value === this.props.value)
                  ?.front
                  ? 36
                  : ".75rem",
            }}
            type="text"
            value={
              !this.state.search &&
              !this.state.showSelectOption &&
              this.props.value
                ? this.getSelectedLabel()
                : this.state.search
            }
            placeholder={this.props.placeholder || ""}
            onFocus={() => {
              const rect = this.inputRef.current ? this.inputRef.current.getBoundingClientRect() : null;
              const ddHeight = 420;
              let pos = null;
              if (rect) {
                // ที่ว่างจริงบน/ล่างช่องเลือก (เผื่อขอบจอ 12px) - ใช้จำกัดความสูงรายการด้วย
                // ไม่งั้นตอนพลิกขึ้นด้านบน (flipUp) รายการที่ยาวกว่าที่ว่างจะทะลุพ้นขอบจอ กดเลือกไม่ได้
                // ขนาดกรอบนอกของ dropdown ที่กินพื้นที่เพิ่มจาก maxHeight ของรายการ (padding 6*2 + border 1*2)
                const chrome = 14;
                const gap = 2;
                const spaceBelow = window.innerHeight - rect.bottom - 12 - gap;
                const spaceAbove = rect.top - 12 - gap;
                const flipUp = rect.bottom + ddHeight > window.innerHeight && spaceAbove > spaceBelow;
                // ที่ว่างจริงสำหรับ "ตัวรายการ" = ที่ว่างด้านที่เลือก - กรอบนอก
                // ถ้าไม่หัก chrome กล่องจะสูงเกินที่ว่าง แล้วตอน flipUp (ยึด bottom) รายการบนสุดจะทะลุพ้นขอบจอ
                const available = (flipUp ? spaceAbove : spaceBelow) - chrome;
                pos = {
                  left: rect.left,
                  top: rect.bottom + gap,
                  bottom: window.innerHeight - rect.top + gap,
                  width: rect.width,
                  // โหมด dropdownAutoWidth: ขยายได้ถึงขอบขวาของจอ (เผื่อ 12px) ถ้ายังไม่พอจะเลื่อนแนวนอนในรายการเอง
                  maxWidth: Math.max(rect.width, window.innerWidth - rect.left - 12),
                  flipUp,
                  // ห้ามเกินที่ว่างจริง (มี scroll ในตัวอยู่แล้ว) แต่ก็ต้องไม่ต่ำจนใช้ไม่ได้
                  maxHeight: Math.max(80, Math.min(400, available)),
                };
              }
              this.setState({
                showSelectOption: true,
                search: "",
                selected: "",
                pos,
              });
              // โหมด remote: เปิดรายการ = ขอชุดตั้งต้น (คำค้นว่าง) ทันที ไม่ต้องรอ debounce
              this.emitSearch("", true);
            }}
            onChange={(e) => {
              this.setState({ search: e.target.value });
              this.emitSearch(e.target.value);
            }}
            disabled={this.props.disabled}
          />
          <div
            style={{
              display: "flex",
              position: "absolute",
              right: 8,
              top: "50%",
            }}
          >
            <div
              style={{
                width: 7,
                height: 1.7,
                backgroundColor: "#232323",
                transform: "rotate(45deg)",
                borderRadius: 1,
              }}
            />
            <div
              style={{
                width: 7,
                height: 1.7,
                backgroundColor: "#232323",
                transform: "rotate(315deg)",
                marginLeft: -3.2,
                borderRadius: 1,
              }}
            />
          </div>
        </div>
        {this.state.showSelectOption && (
          <div
            id="select-tool"
            style={{
              minWidth: 150,
              ...(this.isAutoWidth()
                ? {
                    width: "max-content",
                    minWidth: this.state.pos ? this.state.pos.width : 150,
                    maxWidth: this.state.pos ? this.state.pos.maxWidth : "100%",
                  }
                : { width: this.state.pos ? this.state.pos.width : "100%" }),
              padding: 6,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "#d2d2d2",
              borderStyle: "solid",
              backgroundColor: "#fdfdfd",
              position: "fixed",
              left: this.state.pos ? this.state.pos.left : 0,
              ...(this.state.pos && this.state.pos.flipUp
                ? { bottom: this.state.pos.bottom }
                : { top: this.state.pos ? this.state.pos.top : 40 }),
              zIndex: 99999,
            }}
          >
            <div
              style={{
                maxHeight: this.state.pos ? this.state.pos.maxHeight : 400,
                overflow: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {options?.filter(this.matchesSearch).length !== 0 &&
                this.props.default !== false && (
                  <div
                    style={{
                      width: "100%",
                      cursor: "pointer",
                      padding: 4,
                      backgroundColor:
                        this.state.selected === "" ? "#dfdfdf" : "#fff0",
                      color: "#000",
                    }}
                    onClick={() => {
                      this.setState({
                        showSelectOption: false,
                        search: "",
                        selected: "",
                        picked: null,
                      });
                      this.props.onChange("", { value: "", label: "" });
                    }}
                    onMouseEnter={() => this.setState({ selected: "" })}
                  >
                    -- กรุณาเลือก --
                  </div>
                )}
              {(() => {
                let lastGroup;
                return options
                  ?.filter(this.matchesSearch)
                  .map((item, index) => {
                    // group เป็น field เสริม (opt-in) — ถ้าไม่ส่งมาจะ render แบบ flat list เหมือนเดิมทุกประการ
                    const showGroupHeader = item.group && item.group !== lastGroup;
                    lastGroup = item.group;
                    return (
                      <React.Fragment key={`option-${index}`}>
                        {showGroupHeader && (
                          <div
                            style={{
                              padding: "6px 4px 2px",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#8a8a8a",
                              textTransform: "uppercase",
                            }}
                          >
                            {item.group}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            width: "100%",
                            cursor: "pointer",
                            padding: 4,
                            paddingLeft: item.group ? 12 : 4,
                            whiteSpace: this.isAutoWidth() ? "nowrap" : "normal",
                            backgroundColor:
                              this.state.selected === item.value
                                ? "#dfdfdf"
                                : "#fff0",
                            color: "#000",
                          }}
                          onClick={() => {
                            this.setState({
                              showSelectOption: false,
                              search: "",
                              selected: "",
                              picked: { value: item.value, label: item.label },
                            });
                            this.props.onChange(item.value, item);
                          }}
                          onMouseEnter={() => this.setState({ selected: item.value })}
                        >
                          {item.front || ""}
                          <span>{item.label}</span>
                          {item.back ? <span style={{ marginLeft: 8 }}>{item.back}</span> : ""}
                        </div>
                      </React.Fragment>
                    );
                  });
              })()}
              {options?.filter(this.matchesSearch).length === 0 && (
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    color: "#2c2c2c",
                  }}
                >
                  {this.props.loading ? "กำลังค้นหา..." : "ไม่พบข้อมูล..."}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// ============================================================================
// BtnIcon Component
// ============================================================================
class BtnIcon extends Component {
  render() {
    const iconMap = {
      print: { bg: "primary", icon: "\uf02f", title: "Print" },
      view: { bg: "success", icon: "\uf06e", title: "View" },
      download: { bg: "info", icon: "\uf019", title: "Download" },
      email: { bg: "secondary", icon: "\uf0e0", title: "Send Email" },
      update: {
        bg: "warning",
        icon: "\uf044",
        title: "Update",
        textColor: "orange",
      },
      approve: { bg: "purple", icon: "\uf52d", title: "Change Status" },
      delete: { bg: "danger", icon: "\uf1f8", title: "Delete" },
      inquiry: { bg: "info", icon: "\uf688", title: "Inquiry" },
      callback: {
        bg: "secondary",
        icon: "\u0043",
        title: "Callback",
        textColor: "dark",
      },
    };

    const config = iconMap[this.props.type];
    if (!config) return null;

    return (
      <div>
        <div
          className={`status bg-${config.bg}-light wpx-25 hpx-25 icon px-1 pointer text-${config.textColor || config.bg} ${this.props.className || ""}`}
          title={this.props.title || config.title}
          onClick={() => this.props.onClick?.()}
        >
          {config.icon}
        </div>
      </div>
    );
  }
}

const t = (key) => {
  // ดึงภาษาปัจจุบันจาก localStorage (ค่าเริ่มต้นเป็น TH)
  const currentLang = localStorage.getItem("lang") || "TH";
  
  // เลือกไฟล์ JSON ตามภาษา
  const translations = currentLang === "EN" ? en : th;

  // คืนค่าจาก JSON ตาม key ถ้าไม่เจอให้คืนค่า key กลับไปตรงๆ
  return translations[key] || key;
};

// ============================================================================
// Exports - Backward Compatibility
// ============================================================================
export {
  // Auth/Storage (legacy)
  profile,
  company,
  tokens,
  required,

  // UI Components
  alert,
  Status,
  DatePicker,
  DateRangePicker,
  Select,
  BtnIcon,

  // API Functions (re-exported from services/api)
  GET,
  // ดึงไฟล์ไบนารี (PDF ของ Service Report) - GET ปกติ parse เป็น JSON ใช้กับไฟล์ไม่ได้
  GET_FILE,
  POST,
  PUT,
  DELETE,
  UPLOAD,
  MAX_UPLOAD_SIZE,
  loading,

  // Utility Functions (re-exported from utils)
  format_date,
  total,
  float,
  toFixed,
  random_charactor,
  copyText,
  params,
  // กรองรายการที่ปิดใช้งานออกจาก dropdown (คงค่าที่เลือกอยู่เดิมไว้เสมอ)
  isOptionActive,
  activeOptions,
  activeTreeOptions,
  // เรียง options ก-ฮ -> a-z (Select เรียกให้เองอยู่แล้ว ใช้กับ <select> native)
  sortOptions,
  compareLabels,
  t
};

// Third-party re-exports
export { default as Swal } from "sweetalert2";
// ปุ่มไอคอนกลมมาตรฐานสำหรับคอลัมน์ "จัดการ" ในตาราง + สไตล์ sticky + ชุดไอคอน
export { default as ActionIconButton, ACTION_ICONS, ACTION_VARIANTS, STICKY_ACTION_STYLE } from "../components/ActionIconButton.js";
export { default as Pagination } from "@mui/material/Pagination";
export { default as Switch } from "react-switch";
export { default as NavbarWarehouse } from "../components/NavbarWarehouse.js";
export { default as NavbarAdmin } from "../components/NavbarAdmin.js";
export { default as NavbarCustomer } from "../components/NavbarCustomer.js";
export { default as NavbarSuperUser } from "../components/NavbarSuperUser.js";
// Modal ที่บังคับ backdrop="static" + ปิด keyboard (กดนอก modal / Esc ไม่ปิด) และมีปุ่ม X มุมบนขวา
const Modal = ({ backdrop = "static", keyboard = false, onHide, children, ...props }) => {
    return (
        <BootstrapModal backdrop={backdrop} keyboard={keyboard} onHide={onHide} {...props}>
            {onHide && (
                <button
                    type="button"
                    onClick={onHide}
                    aria-label="Close"
                    className="d-flex align-items-center justify-content-center border-0 rounded-circle position-absolute"
                    style={{
                        top: "12px",
                        right: "12px",
                        width: "32px",
                        height: "32px",
                        zIndex: 5,
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                        color: "#dc3545",
                        fontSize: "16px",
                        cursor: "pointer",
                    }}
                >
                    <span className="icon">{"\uf00d"}</span>
                </button>
            )}
            {children}
        </BootstrapModal>
    );
};
Modal.Header = BootstrapModal.Header;
Modal.Body = BootstrapModal.Body;
Modal.Footer = BootstrapModal.Footer;
Modal.Title = BootstrapModal.Title;
Modal.Dialog = BootstrapModal.Dialog;
export { Modal };
export { default as Resizer } from "react-image-file-resizer";
export { saveAs } from "file-saver";
export * as XLSX from "xlsx";
